/* eslint-disable react/function-component-definition */
import clsx from 'clsx'
import { ComponentType, ReactElement, ReactNode } from 'react'
import styleUtils from 'shared-components/styles/util.module.css'
import type {
  DSAltBestListPage,
  DSBest,
  DSResource,
  NCComponentInterface,
} from 'shared-definitions/types'
import { useArticleViewed } from '../hooks/use-article-viewed'
import { useTaglessAd } from '../hooks/use-tagless-ad'
import ScrollEventsListener from '../service/ScrollEventsListener'
import BreadCrumbs from '../ui/BreadCrumbs'
import ContentLayout from '../ui/ContentLayout'
import Disclaimer from '../ui/Disclaimer'
import GoogleButtons from '../ui/GoogleButtons'
import PostShortLine from '../ui/PostShortLine'
import PostTitle from '../ui/PostTitle'
import SplitterLine from '../ui/SplitterLine'
import AltBestCards from './AltBestCards'
import styles from './AltBestListTemplate.module.css'
import { PostContext } from '../contexts/PostContext'
import TocLayoutAlt from '../ui/TocLayoutAlt'
import ArticleConversation from '../comments/conversation/ArticleConversation'

interface AltBestListTemplateProps<T extends DSResource> {
  page: DSAltBestListPage<T>
  nc: ComponentType<NCComponentInterface<T>>
  /** A row under each "Our picks" card's buttons (SoundGuys: the pick's action row). */
  cardFooterSlot?: (card: DSBest) => ReactNode
  /** On the page wrapper — a site's own type tokens for the shared cards (SoundGuys). */
  className?: string
  /**
   * The page mounts another fixed bottom surface (SoundGuys' LabCompare drawer/pill): no mobile
   * conversation bar (`ArticleConversation` `hideBar`).
   */
  hideConversationBar?: boolean
}

function AltBestListTemplate<T extends DSResource>({
  page,
  nc: NC,
  cardFooterSlot,
  className,
  hideConversationBar,
}: AltBestListTemplateProps<T>): ReactElement {
  const { sections } = PostContext.useContainer()
  useTaglessAd()

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={clsx(styleUtils.wrapper, className)}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={styles.breadCrumbs} />
        <PostTitle title={page.title} subtitle={page.subtitle} />
        <PostShortLine post={page} className={styles.shortLine} />
        <SplitterLine className={styles.splitter} />
        <div className={styles.topBlock}>
          <aside className={styles.topAside}>
            {page.leftTopContent.map((item, index) => (
              <NC key={index + 1} data={item} tpl="sidebar" index={index} />
            ))}
            <GoogleButtons data={page.googleButtons} />
          </aside>
          <div className={styles.topContent}>
            <div className={styles.contentTitle}>{page.cardsTitle}</div>
            <AltBestCards
              cards={page.bestList}
              showMoreLabel={page.showMoreLabel}
              cardFooterSlot={cardFooterSlot}
            />
          </div>
        </div>
        <SplitterLine className={styles.splitter2} />
        {sections && (
          <TocLayoutAlt tableOfContents={sections}>
            {page.blocks.map((item, index) => (
              <NC key={index + 1} data={item} tpl="text" index={index} />
            ))}
            {page.meta.engagementContainerId ? (
              <ArticleConversation className={styles.comments} hideBar={hideConversationBar} />
            ) : null}
          </TocLayoutAlt>
        )}
        {sections === null ? (
          <ContentLayout>
            {page.blocks.map((item, index) => (
              <NC key={index + 1} data={item} tpl="text" index={index} />
            ))}
            {page.meta.engagementContainerId ? (
              <ArticleConversation className={styles.comments} hideBar={hideConversationBar} />
            ) : null}
          </ContentLayout>
        ) : null}
      </div>
    </>
  )
}

export default AltBestListTemplate
